import { useState, useCallback, useMemo } from "react";
import { useDropzone } from "react-dropzone";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { ImagePlus, X } from "lucide-react";
import { useAuth } from "@/providers/auth-provider";

interface ImageUploadProps {
  onUpload: (file: File, service: "imgbb" | "r2", resizeOption: string) => void;
  locale?: string;
}

const localeText = {
  en: {
    icon: "Upload",
    title: "Upload Image",
    description: "Drag 'n' drop an image here, or click to select an image.",
    isDragActive: "Drop the image here ...",
    uploadWarning:
      "Warning: Uploaded images will be publicly accessible via URL. Do not upload images containing sensitive information.",
    button: "Upload",
    ImageUploadService: "Select Image Upload Service",
    ImageUploadServiceImgBB: "ImgBB (Slow, Permanent)",
    ImageUploadServiceR2: "R2 (Fast, 90-day)",
    UploadWarningImgBB:
      "⏱️ Upload may be slow but images are stored permanently.",
    UploadWarningR2: "⚡ Fast upload but images will be deleted after 90 days.",
    ImageResize: "Image Resizing",
    ResizeOption512: "512px (Small)",
    ResizeOption1024: "1024px (Default)",
    ResizeOption2048: "2048px (Large)",
    ResizeOptionOriginal: "Original",
    LoginRequired: "(Login required)",
  },
  ko: {
    icon: "업로드",
    title: "이미지 업로드",
    description: "이미지를 여기에 끌어다 놓거나, 클릭하여 파일을 선택하세요",
    isDragActive: "이미지를 여기에 놓으세요...",
    uploadWarning:
      "주의: 업로드된 이미지는 누구나 접근 가능한 공개 URL로 저장됩니다. 민감한 정보가 포함된 이미지는 업로드하지 마세요.",
    button: "업로드",
    ImageUploadService: "이미지 업로드 서비스 선택",
    ImageUploadServiceImgBB: "ImgBB (느림, 영구저장)",
    ImageUploadServiceR2: "R2 (빠름, 90일저장)",
    UploadWarningImgBB: "⏱️ 업로드 속도가 느리지만 영구적으로 저장됩니다.",
    UploadWarningR2: "⚡ 업로드 속도가 빠르지만 90일 후 자동으로 삭제됩니다.",
    ImageResize: "이미지 리사이징",
    ResizeOption512: "512px (작게)",
    ResizeOption1024: "1024px (기본)",
    ResizeOption2048: "2048px (크게)",
    ResizeOptionOriginal: "원본",
    LoginRequired: "(로그인 필요)",
  },
} as const;

export function ImageUpload({ onUpload, locale = "en" }: ImageUploadProps) {
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [preview, setPreview] = useState<string | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [uploadService, setUploadService] = useState<"imgbb" | "r2">("r2");
  //@ts-ignore
  const [resizeOption, setResizeOption] = useState<string>("0");

  const text = useMemo(() => {
    return localeText[locale as keyof typeof localeText] ?? localeText["en"];
  }, [locale]);

  const onDrop = useCallback((acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    setFile(file);
    setPreview(URL.createObjectURL(file));
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { "image/*": [] },
  });

  const handleUpload = () => {
    if (file) {
      onUpload(file, uploadService, resizeOption);
      setIsOpen(false);
      setPreview(null);
      setFile(null);
    }
  };

  // R2는 로그인 필요
  const canUseR2 = !!user;

  // 서비스별 경고 문구
  const serviceWarning =
    uploadService === "imgbb" ? text.UploadWarningImgBB : text.UploadWarningR2;

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost">
          <ImagePlus className="h-4 w-4 mr-1" />
          {text.icon}
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px] max-h-[90dvh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{text.title}</DialogTitle>
          <DialogDescription className="sr-only">
            {text.description}
          </DialogDescription>
        </DialogHeader>

        {/* 업로드 서비스 선택 */}
        <div className="space-y-2">
          <Label>{text.ImageUploadService}</Label>
          <RadioGroup
            value={uploadService}
            onValueChange={(value) => setUploadService(value as "imgbb" | "r2")}
          >
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="r2" id="r2" disabled={!canUseR2} />
              <Label
                htmlFor="r2"
                className={
                  canUseR2 ? "cursor-pointer" : "cursor-not-allowed opacity-50"
                }
              >
                {text.ImageUploadServiceR2}
                {!canUseR2 && ` ${text.LoginRequired}`}
              </Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="imgbb" id="imgbb" />
              <Label htmlFor="imgbb" className="cursor-pointer">
                {text.ImageUploadServiceImgBB}
              </Label>
            </div>
          </RadioGroup>
        </div>

        {/* 리사이즈 옵션 (ImgBB만 적용) */}
        {/*uploadService === "imgbb" && (
          <div className="space-y-2">
            <Label>{text.ImageResize}</Label>
            <Select value={resizeOption} onValueChange={setResizeOption}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="512">{text.ResizeOption512}</SelectItem>
                <SelectItem value="1024">{text.ResizeOption1024}</SelectItem>
                <SelectItem value="2048">{text.ResizeOption2048}</SelectItem>
                <SelectItem value="0">{text.ResizeOptionOriginal}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )*/}

        {/* 서비스별 경고 문구 */}
        <div className="text-sm bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-700 rounded-lg p-3 text-yellow-800 dark:text-yellow-200">
          {serviceWarning}
        </div>

        {/* 파일 드롭존 */}
        <div
          {...getRootProps()}
          className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors
            ${
              isDragActive
                ? "border-primary bg-primary/10"
                : "border-zinc-300 dark:border-zinc-700 hover:border-primary"
            }`}
        >
          <input {...getInputProps()} />
          {isDragActive ? (
            <p className="text-primary">{text.isDragActive}</p>
          ) : (
            <p className="text-zinc-500 dark:text-zinc-400">
              {text.description}
            </p>
          )}
        </div>

        {/* 미리보기 */}
        {preview && (
          <div className="mt-4 relative max-h-[40dvh] overflow-auto">
            <img
              src={preview}
              alt="Preview"
              className="w-auto mx-auto rounded-lg"
            />
            <Button
              variant="destructive"
              size="icon"
              className="absolute top-2 right-2"
              onClick={() => {
                setPreview(null);
                setFile(null);
              }}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        )}

        {/* 업로드 버튼 */}
        <Button onClick={handleUpload} disabled={!file} className="mt-4 w-full">
          {text.button}
        </Button>
      </DialogContent>
    </Dialog>
  );
}
